import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId, useState, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { reviewQueueKey } from '@/entities/deck';
import { learningKeys } from '@/entities/learning';
import { updateLearningSettings } from '@/shared/api/learning';
import type { LearningSettings } from '@/shared/api/types';
import { Button, ErrorMessage } from '@/shared/ui';

import styles from './BudgetForm.module.css';

/** Minutes a day offered as one tap; any other amount from 5 to 240 is typed. */
export const budgetChoices = [15, 30, 45] as const;

/** Bounds of a typed budget, matching the server. */
export const budgetBounds = { min: 5, max: 240 } as const;

export interface BudgetFormProps {
  settings: LearningSettings;
  submitLabel?: string;
  onSaved?: (settings: LearningSettings) => void;
}

/**
 * The only setting of the scheduler: how many minutes a day the learner has.
 * Everything else, including how many new cards to give, follows from it.
 */
export function BudgetForm({ settings, submitLabel, onSaved }: BudgetFormProps): React.JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const saved = settings.daily_minutes ?? 15;
  const preset = (budgetChoices as readonly number[]).includes(saved);
  const [choice, setChoice] = useState<number | 'other'>(preset ? saved : 'other');
  const [custom, setCustom] = useState(preset ? '' : String(saved));
  const hintId = useId();

  const minutes = choice === 'other' ? Number(custom) : choice;

  const mutation = useMutation({
    mutationFn: () => updateLearningSettings({ daily_minutes: minutes, goal_date: null }),
    onSuccess: async (stored) => {
      onSaved?.(stored);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: learningKeys.all }),
        queryClient.invalidateQueries({ queryKey: reviewQueueKey }),
      ]);
    },
  });

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault();
    mutation.mutate();
  };

  const options: (number | 'other')[] = [...budgetChoices, 'other'];

  return (
    <form className={styles.form} onSubmit={submit}>
      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>{t('budget.minutesLabel')}</legend>
        <div className={styles.choices}>
          {options.map((option) => (
            <label
              key={option}
              className={[styles.choice, option === choice ? styles.chosen : ''].join(' ')}
            >
              <input
                type="radio"
                name="daily-minutes"
                value={option}
                checked={option === choice}
                onChange={() => {
                  setChoice(option);
                }}
              />
              {option === 'other' ? t('budget.other') : t('budget.minutes', { count: option })}
            </label>
          ))}
        </div>
      </fieldset>

      {choice === 'other' && (
        <div className={styles.custom}>
          <label className={styles.customField}>
            <span>{t('budget.customLabel')}</span>
            <input
              aria-describedby={hintId}
              type="number"
              inputMode="numeric"
              className={styles.number}
              required
              min={budgetBounds.min}
              max={budgetBounds.max}
              step={1}
              value={custom}
              onChange={(event) => {
                const { value } = event.target;
                setCustom(value);
              }}
            />
          </label>
          <span id={hintId} className={styles.hint}>
            {t('budget.customHint', { min: budgetBounds.min, max: budgetBounds.max })}
          </span>
        </div>
      )}

      {mutation.isError && <ErrorMessage error={mutation.error} />}
      {mutation.isSuccess && onSaved === undefined && (
        <p className={styles.ok} role="status">
          {t('common.saved')}
        </p>
      )}

      <Button type="submit" variant="primary" disabled={mutation.isPending}>
        {submitLabel ?? t('common.save')}
      </Button>
    </form>
  );
}
